// src/components/CartWidget.js
import React, { useEffect, useMemo, useState, useCallback, useContext } from 'react';
import { FiShoppingCart } from 'react-icons/fi';
import { useCart } from '../CartContext';
import { useNavigate } from 'react-router-dom';
import { AppContext } from '../AppContext';

const CURRENCY = 'USD';
const STORAGE_KEY = 'sf_cart_v1';
const CHECKOUT_PREF_KEY = 'sf_checkout_wallet_pref'; // 'wallet' | 'stripe'

export default function CartWidget() {
  const { qty, lines, updateCart } = useCart();
  const [open, setOpen] = useState(false);
  const [askWallet, setAskWallet] = useState(false); // prompt modal
  const navigate = useNavigate();

  // Get currentUser from AppContext (empty object when not logged in)
  const { currentUser } = useContext(AppContext);
  const signedIn = !!(currentUser && Object.keys(currentUser || {}).length > 0);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
      if (saved && Array.isArray(saved.lines) && saved.lines.length > 0) {
        const totalQty = saved.lines.reduce((s, l) => s + Number(l.qty || 0), 0);
        updateCart?.({ qty: totalQty, lines: saved.lines });
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalCents = useMemo(
    () => (Array.isArray(lines) ? lines.reduce((sum, l) => sum + Number(l.priceCents || 0) * Number(l.qty || 0), 0) : 0),
    [lines]
  );
  const displayQty = useMemo(
    () => (Array.isArray(lines) ? lines.reduce((s, l) => s + Number(l.qty || 0), 0) : (qty || 0)),
    [lines, qty]
  );

  const fmtUSD = (cents) =>
    (Number(cents) / 100).toLocaleString(undefined, { style: 'currency', currency: CURRENCY });

  const persist = useCallback((nextLines) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ lines: nextLines })); } catch {}
  }, []);

  const setLines = useCallback(
    (nextLines) => {
      const nextQty = nextLines.reduce((s, l) => s + Number(l.qty || 0), 0);
      persist(nextLines);
      updateCart?.({ qty: nextQty, lines: nextLines });
    },
    [persist, updateCart]
  );

  const setItemQty = useCallback(
    (lineId, newQty) => {
      if (!Array.isArray(lines)) return;
      let next = lines.map((l) => (l.lineId === lineId ? { ...l, qty: Math.max(0, Number(newQty) || 0) } : l));
      next = next.filter((l) => l.qty > 0);
      setLines(next);
    },
    [lines, setLines]
  );

  const removeLine = useCallback(
    (lineId) => {
      if (!Array.isArray(lines)) return;
      const next = lines.filter((l) => l.lineId !== lineId);
      setLines(next);
    },
    [lines, setLines]
  );

  const clearCart = useCallback(() => {
    persist([]);
    updateCart?.({ qty: 0, lines: [] });
  }, [persist, updateCart]);

  // Open the wallet/stripe choice prompt instead of navigating directly
  const goToCheckout = useCallback(() => {
    if (!Array.isArray(lines) || lines.length === 0) return;
    setOpen(false);
    if (signedIn) {
      setAskWallet(true);
    } else {
      navigate('/login', { state: { from: '/checkout' } });
    }
  }, [lines, signedIn, navigate]);

  const chooseWallet = useCallback(() => {
    try { localStorage.setItem(CHECKOUT_PREF_KEY, 'wallet'); } catch {}
    setAskWallet(false);
    navigate('/checkout');
  }, [navigate]);

  const chooseStripe = useCallback(() => {
    try { localStorage.setItem(CHECKOUT_PREF_KEY, 'stripe'); } catch {}
    setAskWallet(false);
    navigate('/checkout');
  }, [navigate]);

  const closePrompt = useCallback(() => setAskWallet(false), []);

  return (
    <>
      {/* Cart icon */}
      <button
        onClick={() => setOpen((v) => !v)}
        aria-label="Cart"
        style={{
          position: 'absolute',
          top: 40,
          left: 22,
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          zIndex: 1000,
        }}
      >
        <FiShoppingCart size={26} color="#fdca05" />
        <span style={{ color: '#fff', fontWeight: 800, fontSize: '0.95rem', marginLeft: 4 }}>
          {displayQty}
        </span>
      </button>

      {/* Drawer */}
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 80,
            left: 56,
            width: 330,
            maxHeight: '80vh',
            overflowY: 'auto',
            background: '#0b0b0b',
            border: '1px solid rgba(255,255,255,.12)',
            borderRadius: 12,
            padding: 14,
            boxShadow: '0 10px 24px rgba(0,0,0,.4)',
            zIndex: 999,
          }}
        >
          <h4 style={{ margin: '2px 0 10px', color: '#fff', fontWeight: 900 }}>Cart</h4>

          {!Array.isArray(lines) || lines.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,.8)', margin: 0 }}>Your cart is empty.</p>
          ) : (
            <>
              {lines.map((l) => (
                <div
                  key={l.lineId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 10,
                    background: 'rgba(255,255,255,.04)',
                    border: '1px solid rgba(255,255,255,.1)',
                    borderRadius: 10,
                    padding: 8,
                  }}
                >
                  <img
                    src={l.image}
                    alt={l.title}
                    style={{ width: 46, height: 46, objectFit: 'cover', borderRadius: 6, border: '1px solid rgba(255,255,255,.12)' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: '#fff', fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {l.title}
                    </div>
                    <div style={{ fontSize: 12, color: '#fdca05', fontWeight: 800 }}>
                      {(Number(l.priceCents) / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
                      <button onClick={() => setItemQty(l.lineId, Number(l.qty) - 1)} style={qtyBtn} aria-label="Decrease quantity">−</button>
                      <div style={{ color: '#fff', fontWeight: 800, minWidth: 20, textAlign: 'center' }}>{l.qty}</div>
                      <button onClick={() => setItemQty(l.lineId, Number(l.qty) + 1)} style={qtyBtn} aria-label="Increase quantity">+</button>
                    </div>
                  </div>
                  <button
                    onClick={() => removeLine(l.lineId)}
                    style={{
                      background: 'transparent',
                      border: '1px solid rgba(255,255,255,.18)',
                      color: '#fff',
                      borderRadius: 10,
                      padding: '4px 8px',
                      cursor: 'pointer',
                      fontWeight: 800,
                    }}
                    aria-label="Remove item"
                  >
                    ✕
                  </button>
                </div>
              ))}

              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, color: '#fff', fontWeight: 900 }}>
                <span>Total</span>
                <span style={{ color: '#fdca05' }}>
                  {(Number(totalCents) / 100).toLocaleString(undefined, { style: 'currency', currency: 'USD' })}
                </span>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button
                  onClick={goToCheckout}
                  style={{
                    flex: 1,
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: '2px solid #fdca05',
                    background: '#fdca05',
                    color: '#000',
                    fontWeight: 900,
                    cursor: 'pointer',
                    boxShadow: '0 6px 18px rgba(128,128,78,.26)',
                  }}
                >
                  Checkout
                </button>
                <button
                  onClick={clearCart}
                  style={{
                    padding: '10px 12px',
                    borderRadius: 12,
                    border: '1px solid rgba(255,255,255,.25)',
                    background: 'transparent',
                    color: '#fdca05',
                    fontWeight: 900,
                    cursor: 'pointer',
                  }}
                >
                  Clear
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Wallet/Stripe choice prompt */}
      {askWallet && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closePrompt}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '92%', maxWidth: 460,
              background: '#0b0b0b',
              border: '1px solid rgba(255,255,255,.15)',
              borderRadius: 14,
              padding: 16,
              boxShadow: '0 10px 30px rgba(0,0,0,.45)'
            }}
          >
            <div style={{ fontWeight: 900, fontSize: '1.05rem', color: '#fff', marginBottom: 8 }}>
              Checkout with my wallet?
            </div>
            <div style={{ color: 'rgba(255,255,255,.85)', marginBottom: 14, lineHeight: 1.4 }}>
              If you use your wallet, your utility tokens will be applied to your order. 
              If the wallet doesn’t cover the full amount, we’ll charge the remainder with Stripe.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={chooseWallet}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: '2px solid #fdca05',
                  background: '#fdca05',
                  color: '#000',
                  fontWeight: 900,
                  cursor: 'pointer',
                  boxShadow: '0 6px 18px rgba(128,128,78,.26)',
                }}
              >
                Yes
              </button>
              <button
                onClick={chooseStripe}
                style={{
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: '1px solid rgba(255,255,255,.25)',
                  background: 'transparent',
                  color: '#fdca05',
                  fontWeight: 900,
                  cursor: 'pointer',
                }}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const qtyBtn = {
  width: 28,
  height: 28,
  borderRadius: 8,
  border: '1px solid rgba(255,255,255,.25)',
  background: 'transparent',
  color: '#fff',
  fontWeight: 900,
  cursor: 'pointer',
};
